/**
 * Render a [SocialBoard] into LLM-readable text describing the agent's local world.
 *
 * The rendering is deliberately stable and reference-first: every post, route, and actor is named by its
 * content-addressed [SocialRef], so a reader can name any of them unambiguously when acting. Missing observations are
 * stated plainly as missing — not hidden — so an agent reasons about a partial local world rather than assuming a
 * complete feed.
 *
 * This renders *only the world*. How a reader is expected to respond is a property of the caller, not of the world:
 * the active agent (MCP / CLI) acts through structured tool calls whose vocabulary comes from `tools/list`, while the
 * passive copy/paste flow ([SummonSession]) appends its own action-line response protocol. Baking the copy/paste
 * grammar in here would contradict the active agent's tool schemas (it observably confused agents reading both).
 */

package com.oasisfeng.invaluable.social

object ContextRenderer {

    /**
     * How many push suggestions the world dump carries. A fixed budget, not a fraction of the pool: the candidate pool
     * spans every visible post, so this section would otherwise grow with the world and turn the dump into the
     * full-render expansion the dog-fooding charter's stop-loss forbids.
     */
    private const val SUGGESTED_PUSH_DIGEST = 6

    /**
     * Produce the world text for [board], optionally introduced by a human [note].
     *
     * When [continuing] is true the text is a *continuation* for a chat that already holds the role from an earlier
     * turn (Milestone A1's multi-turn flow): it restates only the refreshed local world, leaning on the LLM
     * conversation's existing context instead of repeating the full preamble.
     *
     * [livePeers] is the count of peer links open at this moment — the one live transport fact a node can honestly
     * observe about itself. It is null when the transport is not running (e.g. a one-shot CLI read of a stored world),
     * which is itself a fact worth stating. It is deliberately the *only* connectivity signal exposed: there are no
     * read receipts and no global presence list (roadmap §7.2/§14.1 non-goals), so a reader never mistakes "I cannot
     * see whether a peer read my post" for "data was lost".
     */
    fun render(board: SocialBoard, note: String? = null, continuing: Boolean = false, livePeers: Int? = null,
               privateRefs: Set<SocialRef> = emptySet()): String = buildString {
        if (continuing) {
            appendLine("# Updated view of your local social world")
            appendLine("You are still actor ${board.me.label}. Only the current state is shown below; the role and action grammar from earlier in this chat still apply.")
        } else {
            appendLine("# Your local social world")
            appendLine("You are actor ${board.me.label}. This is only what your node can currently see; it is not a global feed.")
        }
        note?.takeIf(String::isNotBlank)?.let { appendLine().appendLine(it.trim()) }

        appendLine().appendLine("## Connectivity (your node's own view, this moment)")
        val others = board.actors.count { !it.isMe }
        if (livePeers != null) {
            appendLine("Peer links open right now: $livePeers. Posts you author propagate over these links; a peer's response reaches you as a new entry in your Feed or Inbox once it syncs.")
        } else {
            appendLine("You are reading your node's stored local world; live peer links are not tracked in this view. Posts you author reach peers only while your node is online, and a peer's response reaches you as a new entry in your Feed or Inbox once it syncs.")
        }
        appendLine("Known actors besides you: $others (listed in full under \"## Known actors\" below). There are no read receipts and no global presence list — you cannot tell whether anyone has read a post, only observe what has synced to you.")

        appendLine().appendLine("## Feed (visible posts)")
        val publicFeed = board.feed.filter { it.ref !in privateRefs }   // your private posts show in their own section
        if (publicFeed.isEmpty()) appendLine("(none)")
        else {
            appendLine(feedLegend(board))
            publicFeed.forEach { appendPost(it, depth = 0) }
        }

        appendLine().appendLine("## My private posts (only you — peers see a missing observation until you share)")
        val mine = board.feed.filter { it.ref in privateRefs }.sortedByDescending { it.backingTotal }
        if (mine.isEmpty())
            appendLine("(none — call remember(\"…\", amount) to keep a private post: an intent, a draft, an open question. " +
                "It is a real post only you can see (peers see a missing observation, never the body or amount); the amount " +
                "you stake is its weight. Later, back([ref]) raises it, publish([ref]) reveals it to everyone, or " +
                "recommend([ref], actor) sends it to someone — that is how a private memory becomes collective.)")
        else {
            appendLine("Only you can see these; their bodies and amounts are withheld from peers until you share. Heaviest first.")
            mine.forEach { appendLine("[${it.ref}] (cost ${it.cost}, backing ${it.backingTotal}) ${it.text ?: "(non-text content)"}") }
            appendLine("→ back([ref]) raises a private post's weight; publish([ref]) reveals it to everyone, or " +
                "recommend([ref], actor) sends it to someone (private → collective).")
        }

        appendLine().appendLine("## Inbox (recommended to you)")
        val pendingInbox = board.inbox.filterNot { it.acted }
        if (pendingInbox.isEmpty()) appendLine("(none)")
        else {
            appendLine("Each line's (cost N) is the quantity the sender staked to route this post to you. Awaiting your response — reply or endorse it.")
            pendingInbox.forEach { item -> appendLine(renderInboxItem(item)) }
        }

        val actedInbox = board.inbox.filter { it.acted }
        if (actedInbox.isNotEmpty()) {
            appendLine().appendLine("## Recently acted (routes you already replied to or endorsed)")
            appendLine("These moved out of Inbox because you already responded — shown so you don't lose track of what you handled; no action needed.")
            actedInbox.forEach { item -> appendLine(renderInboxItem(item)) }
        }

        appendLine().appendLine("## Suggested pushes (recommend a post to a likely backer)")
        if (board.suggestedRoutes.isEmpty()) appendLine("(none)")
        else {
            appendLine("Each line proposes routing a visible post to an actor whose own commitments make them a likely backer. Recommending costs the suggested stake and creates a route you own — it appreciates if they engage, and paying for it buys you a share of the post itself, whether or not you already held one. You choose whether and how much to stake.")
            // Only the strongest few: this section rides inside every get_context, and the candidate pool is now every
            // visible post — letting it grow with the pool is the full-dump expansion dog-fooding.md#过门与止损e-系列
            // forbids. `suggest_recommend` is the read that shows more, and per post it shows all.
            val shown = board.suggestedRoutes.take(SUGGESTED_PUSH_DIGEST)
            shown.groupBy { it.subjectRef }.forEach { (subjectRef, routes) ->
                appendLine("- post [$subjectRef] \"${routes.first().subjectText ?: "(non-text content)"}\"" +
                    " (${holdingNote(board.valuation.shareOf(subjectRef))}):")
                routes.forEach { appendLine("    -> ${it.to.label} (${it.reason}; suggested stake ${it.suggestedCost})") }
            }
            val rest = board.suggestedRoutes.size - shown.size
            if (rest > 0) appendLine("  … and $rest weaker candidate(s) — call suggest_recommend to see more, or suggest_recommend(post=<ref>) for one post's full audience.")
        }

        appendLine().appendLine("## Missing (committed near you, body not visible)")
        if (board.missing.isEmpty()) appendLine("(none)")
        else board.missing.forEach { notice ->
            val who = notice.issuer?.label ?: "an unknown actor"
            appendLine("- $who committed something whose body you cannot see (a missing observation, not lost data)")
        }

        appendLine().appendLine("## Economy (measured in your own currency)")
        if (!board.valuation.priced) {
            appendLine("No usable price frame from your vantage yet: your node hasn't staked anything it can price — commit to a post (author, comment, or back one) and you measure everything in your own currency. (Once you have staked, your own stake anchors a frame; only a vanishing stake against far-larger ones leaves it unpriced.) Frame-free numbers (backing, your ownership share, issuance) are still exact.")
        } else {
            appendLine("Prices are relative: your own currency is the unit, so you read price 1. An actor's price is what one unit of their currency is worth in yours; assets = price × issuance (their total staked weight). A low price means little of the network's conserved value has settled on that actor per unit they staked (weak validation so far), not that they are worthless — it can rise as others build on their contributions.")
            board.actors.forEach { tag ->
                val price = board.valuation.priceOf(tag.ref)?.let(::formatAmount) ?: "—"
                val assets = board.valuation.assetsOf(tag.ref)?.let(::formatAmount) ?: "—"
                val issuance = board.valuation.issuanceOf(tag.ref)?.let(::formatAmount) ?: "0"
                appendLine("- ${tag.label}: price $price, assets $assets, issuance $issuance")
            }
        }

        appendLine().appendLine("## Known actors")
        appendLine(if (board.actors.isEmpty()) "(none)" else board.actors.joinToString(", ") { it.label })
        // A name-collision note is a render-time reading of the *currently projected* display names — never
        // authentication, never stored state. Names may legitimately repeat in a decentralized world; the
        // content-addressed [ref] is the only identity, so a reader must discover the ambiguity here rather
        // than after a misdirected action.
        board.actors.filter { it.displayName != null }.groupBy { it.displayName }.values
            .filter { it.size > 1 }.forEach { group ->
                appendLine("Name collision: \"${group.first().displayName}\" is currently the display name of " +
                    "${group.size} distinct actors — ${group.joinToString(", ") { "[${it.ref.value}]" }}. A display " +
                    "name is an unauthenticated label, not an identity; always address actions to the exact [ref].")
            }
    }

    /**
     * The legend for the feed's economic parenthetical, grown to match what the world actually carries: `value` only
     * when a measuring frame exists, `your share` only when the local actor holds a stake in some visible post. Each
     * dimension states its meaning the first (and only) place it appears — the same one-line-per-dimension contract the
     * cost/backing legend already follows.
     */
    private fun feedLegend(board: SocialBoard): String {
        val priced = board.valuation.priced
        val anyShare = board.feed.any { it.holdsShare() }
        val template = buildString {
            append("(cost C, backing B")
            if (priced) append(", value V")
            if (anyShare) append(", your share S%")
            append(")")
        }
        return buildString {
            append("Each post reads $template: ")
            append("cost C is the quantity the author staked to author it; ")
            append("backing B is the running total of every commitment toward it so far — the author's own cost plus whatever others have since added (so B equals C until someone else backs it)")
            if (priced) append("; value V is the post's net worth measured in your own currency (your own price is 1)")
            if (anyShare) append("; your share S% is the conserved fraction of that value you hold from authoring or backing it")
            append(". Higher backing means more of the network has put weight behind it.")
        }
    }

    private fun renderInboxItem(item: InboxItem): String {
        val subject = item.subject?.let { "post [${it.ref}] \"${it.text ?: "(no text)"}\"" } ?: "post [${item.subjectRef}] (not visible here)"
        return "[${item.ref}] from ${item.from.label} -> $subject (cost ${item.cost})"
    }

    private fun PostCard.holdsShare(): Boolean = yourShare > 0.0 || replies.any { it.holdsShare() }

    private fun StringBuilder.appendPost(card: PostCard, depth: Int) {
        val indent = "  ".repeat(depth)
        val economics = buildString {
            card.value?.let { append(", value ${formatAmount(it)}") }
            if (card.yourShare > 0.0) append(", your share ${formatPercent(card.yourShare)}")
        }
        appendLine("$indent[${card.ref}] by ${card.author.label} (cost ${card.cost}, backing ${card.backingTotal}$economics)")
        appendLine("$indent  ${card.text ?: "(non-text content)"}")
        card.replies.forEach { appendPost(it, depth + 1) }
    }
}
